import React, { useEffect, useState } from 'react';
import 'react-credit-cards/es/styles-compiled.css';
import TagManager from 'react-gtm-module';
import { Field, reduxForm } from 'redux-form';
import { FormCheckbox, FormNumber, PaymentMethod } from '.';
import focusInputOnError from '../helpers/focusInputOnError';
import validate from '../helpers/paymentFormValidate';
import { formatPrice } from '../helpers/utils';
import { IPlan } from '../store/Quote';
import Loading from './Loading';

interface IPaymentFormProps {
  changePaymentMethod: (method: string) => void;
  documentId: string;
  emitting: boolean;
  fullName: string;
  handleSubmit?: (payload: any) => void;
  onSubmit: any;
  previousPage: () => void;
  plan: IPlan;
}

export function PaymentForm(props: IPaymentFormProps) {
  const {
    changePaymentMethod,
    documentId,
    handleSubmit,
    previousPage,
    emitting,
    plan,
  } = props;
  const [collapseCMR, setCollapseCMR] = useState(true);

  const toggleCollapse = (method: string) => {
    if (method === 'CMR') {
      setCollapseCMR(true);
      changePaymentMethod(method);
    } else {
      setCollapseCMR(false);
      changePaymentMethod(method);
    }
  };

  useEffect(() => {
    if (process.env.REACT_APP_ENVIRONMENT === 'production') {
      const tagManagerArgs = {
        dataLayer: {
          event: 'vpvPago',
        },
        dataLayerName: 'datalayer',
      };

      TagManager.dataLayer(tagManagerArgs);
    }
  }, []);

  const step3AddActive = (element:any) => {
    element.classList.add('active');
  };

  step3AddActive(document.getElementsByClassName('step-3')[0]);

  return (
    <div className="container">
      {!emitting && (
        <div className="row">
          <div className="col-12 mb50">
            <div className="row">
              <div className="d-none">
                <PaymentMethod
                  active={collapseCMR}
                  imgUrl="//sfestaticos.blob.core.windows.net/guideline/payment-logos/CMR.svg"
                  name="CMR"
                  description="CMR / Visa CMR / Mastercard CMR"
                  select={toggleCollapse}
                />
              </div>

              <div className="col-12 mt30">
                <form onSubmit={handleSubmit} noValidate={true}>
                  <Field name="paymentMethod" component="input" type="hidden" />
                  <div className="col-12 wizard-card">
                    <div className="row">
                      <div className="col-12 col-sm-6">
                        <div className="row">
                          <div className="col-8 col-sm-7 mx-auto mb20">
                            <img
                              className="img-fluid w-100"
                              src="//sfestaticos.blob.core.windows.net/otl/mexico/accidentes-personales/pdf/card-dummy.svg"
                              alt="Logo CMR"
                            />
                          </div>
                          <div className="w-100" />
                          <div className="col-12 col-sm-6 form-group">
                            <div>
                              <span className="text-bold">
                                CURP propietario tarjeta
                              </span>
                              <p>{documentId}</p>
                            </div>
                          </div>
                          <div className="w-100 d-none d-sm-block" />
                          <div className="col-12 col-sm-8 form-group mb0">
                            <label htmlFor="cmrCard">
                              Últimos 4 números de tú tarjeta Falabella Soriana
                            </label>
                            <Field
                              name="cmrCard"
                              placeholder="1234"
                              inputFormat="####"
                              component={FormNumber}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-12 col-sm-6 border-left pt20 pb20 product-resume-payment">
                        <div className="row">
                          <article className="col-12 col-sm-8 mx-auto">
                            <div className="product-card no-shadow mb0">
                              <section className="recommended-section">
                                <h3 className="mb20">{plan.name}</h3>
                                <hr />
                              </section>
                              <section>
                                <figure>
                                  <img
                                    className="w-50"
                                    src="//sfestaticos.blob.core.windows.net/guideline/companies/chubb-filled.svg"
                                    alt="Metlife"
                                  />
                                </figure>
                                <hr />
                                <p className="px-20 text-dark-gray mb5">
                                  Pago mensual
                                </p>
                                <h3 className="product-card--price">
                                  <img
                                    src="//sfestaticos.blob.core.windows.net/pmc/vida/assets/svg/money.svg"
                                    alt="Simbolo de peso"
                                    width="20px"
                                  />
                                  {formatPrice(plan.premium_monthly)}
                                </h3>
                                <hr />
                                <a
                                  className="btn-link text-green"
                                  href="//sfestaticos.blob.core.windows.net/otl/mexico/accidentes-personales/pdf/accidentes-personales.pdf"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Consulta Condiciones Generales del producto
                                </a>
                              </section>
                            </div>
                          </article>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12 col-sm-auto form-group mx-auto mt20">
                      <div className="w-100 mb10">
                        <Field
                          name="companyCheck"
                          component={FormCheckbox}
                          text="Acepto los términos y condiciones del "
                          textUrl="Aviso de Privacidad"
                          textAfterLink=" de Chubb Seguros México S.A."
                          url="https://www.chubb.com/content/dam/chubb-sites/chubb-com/mx-es/footer/privacy-notices/documents/pdf/aviso-de-privacidad-asegurados-chubb-seguros-mexico.pdf"
                        />
                      </div>
                      <div className="w-100 mb15">
                        <Field
                          name="consentCheck"
                          component={FormCheckbox}
                          text="Otorgo mi consentimiento para la contratacion del producto de seguro cía electrónica y para que se me entregue la documentacion contractual, a través del correo electronico proporcionado."
                        />
                      </div>
                      <div className="w-100">
                        <Field
                          name="legalTermsCheck"
                          component={FormCheckbox}
                          text="Acepto los "
                          textUrl="Términos y Condiciones"
                          textAfterLink=" de Servicios Financieros Soriana S.A.P.I. de C.V. SOFOM E.N.R."
                          url="https://sfestaticos.blob.core.windows.net/otl/mexico/accidentes-personales/pdf/terminos-y-condiciones.pdf"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row justify-content-center mt50 buttons-container">
                    <div className="col-12 col-sm-5 col-md-4">
                      <button
                        className="btn white back-to-dpsback-to-dps-from-payment-from-payment"
                        type="button"
                        onClick={previousPage}
                      >
                        Volver
                      </button>
                    </div>
                    <div className="col-12 col-sm-5 col-md-4">
                      <button className="btn primary pay-button" type="submit">
                        Pagar
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
      {emitting && <Loading />}
    </div>
  );
}

export default reduxForm<any, IPaymentFormProps>({
  form: 'proposal',
  onSubmitFail: focusInputOnError,
  validate,
})(PaymentForm);
